/**
 * Foundation Context Engine · hook di prospettiva.
 *
 *   `useEffectiveRole()`     - ritorna il codice ruolo effettivo
 *   `useEffectiveContext()`  - ritorna il context completo + real user
 *
 * SEPARAZIONE ARCHITETTURALE (permanente):
 *   - `user.role` (da `useAuth`)   = IDENTITÀ REALE (JWT / RBAC server-side).
 *     Deve rimanere invariato: NON viene mai sovrascritto dal Context Engine.
 *   - `useEffectiveRole()`         = CONTESTO OPERATIVO (Foundation Context).
 *     E' cio' che la UI usa per decidere quali elementi mostrare (menu,
 *     tab, pulsanti, layout). Se il Context Engine non ha ancora definito
 *     un contesto (login iniziale, offline), l'hook ritorna il ruolo reale
 *     dell'utente autenticato — retro-compat totale.
 *
 * Il backend NON legge mai da questo hook: continua a validare i permessi
 * via `auth.py` e i decorator RBAC esistenti.
 */
import { useAuth } from "@/contexts/AuthContext";
import { useSuiteContext } from "../SuiteContextProvider";


/**
 * Ritorna il codice del ruolo effettivo (prospettiva UI).
 *
 * Ordine di precedenza:
 *   1. `context.role.code` se il Context Engine ha caricato il contesto;
 *   2. `user.role` (ruolo autenticato) come fallback.
 *
 * @returns {string|null} codice ruolo (es. "employee") o null.
 */
export function useEffectiveRole() {
  const { user } = useAuth();
  const { context } = useSuiteContext();
  return context?.role?.code || user?.role || null;
}


/**
 * Ritorna un descrittore compatto della "prospettiva" corrente.
 *
 * @returns {{
 *   role: string|null,             // ruolo effettivo (Context Engine o reale)
 *   realRole: string|null,         // ruolo reale (immutabile)
 *   companyId: string|null,        // azienda attiva
 *   moduleKey: string|null,        // modulo attivo
 *   isImpersonating: boolean,      // true se in simulazione
 *   effectiveUserId: string|null,  // id utente sotto cui la UI opera
 *   realUserId: string|null,       // id utente autenticato
 *   isPerspectiveActive: boolean,  // true se il Context Engine e' attivo
 * }}
 */
export function useEffectiveContext() {
  const { user } = useAuth();
  const { context } = useSuiteContext();
  const active = !!context;
  return {
    role: context?.role?.code || user?.role || null,
    realRole: user?.role || null,
    companyId: context?.company?.id || user?.company_id || null,
    moduleKey: context?.module?.app_key || null,
    isImpersonating: !!context?.is_impersonating,
    effectiveUserId: context?.effective_user?.id || user?.id || null,
    realUserId: user?.id || null,
    isPerspectiveActive: active,
  };
}


/** Alias richiesto dai requisiti (semantico per la UI). */
export const useCurrentRole = useEffectiveRole;
